import { useMemo } from 'react';

import type { ConsumerBridgeProps } from 'src/concierge/contract';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useUserButton } from 'src/dispensary/core-menu/components/header/user-button/use-user-button';
import { Session, useSessionResetWatcher } from 'src/utils/session';

import { useCommerceActions } from './use-commerce-actions';
import { useCommerceState } from './use-commerce-state';
import { useRefetchOnStoreClear } from './use-refetch-on-store-clear';

export function useConciergeBridge(): ConsumerBridgeProps {
  // Refetch bridge data after the marketplace clears the Apollo store on login/logout.
  useRefetchOnStoreClear();
  const { dispensary } = useDispensary();
  const userButton = useUserButton();
  const commerce = useCommerceState(userButton);
  const actions = useCommerceActions(userButton);
  // Forces the memo below to recompute marketplaceSessionId after Session.reset().
  const sessionResetWatcher = useSessionResetWatcher();

  return useMemo(
    () => ({
      dispensaryId: dispensary.id,
      // Retailer.enterpriseId is nullable behind a non-null retailer, so standalone dispensaries
      // yield null here; normalize to undefined so the prop has a single absent-representation.
      enterpriseId: dispensary.retailer?.enterpriseId ?? undefined,
      marketplaceSessionId: Session.getInstance().data.id,
      commerce,
      actions,
    }),
    // sessionResetWatcher is intentionally unused above; see the const declaration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispensary.id, dispensary.retailer?.enterpriseId, commerce, actions, sessionResetWatcher]
  );
}
